import React, { useEffect, useState } from "react";
import { Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { FiBarChart2, FiPlus } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useScopeId } from "@core/hooks/useRouteParamId";
import InfoTooltip from "@core/components/InfoTooltip";
import StatusPill from "@core/components/StatusPill";
import { useFinanceAllocationRuleAction } from "@features/admin/finance/allocation-rule/hooks/useFinanceAllocationRuleAction";
import { useFinanceAllocationRuleOptions } from "@features/admin/finance/allocation-rule/hooks/useFinanceAllocationRuleOptions";
import { FinanceAllocationRule } from "@features/admin/finance/allocation-rule/types/financeAllocationRule";
import DeductibleConfigEditor from "@features/admin/finance/components/DeductibleConfigEditor";
import { showSaveWarnings, useDeductibleConfigForm } from "@features/admin/finance/hooks/useDeductibleConfigForm";

type FieldErrors = Partial<Record<string, string[]>>;

interface BasicForm {
    rule_name: string;
    rule_description: string;
    kpi_id: number;
}

const EMPTY_FORM: BasicForm = { rule_name: "", rule_description: "", kpi_id: 0 };

interface AllocationRuleFormModalProps {
    show: boolean;
    onHide: () => void;
    /** Null for create. */
    rule: FinanceAllocationRule | null;
    onSaved?: () => void | Promise<void>;
    testId: string;
}

const AllocationRuleFormModal: React.FC<AllocationRuleFormModalProps> = ({ show, onHide, rule, onSaved, testId }) => {
    const isEdit = rule !== null;
    const prefix = isEdit ? "update_" : "";
    const resolvedNationId = useScopeId() ?? 0;
    const [form, setForm] = useState<BasicForm>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doCreate, doUpdate } = useFinanceAllocationRuleAction();
    const { kpis, kpisLoading, kpisError, fetchKpis } = useFinanceAllocationRuleOptions();
    const deductibles = useDeductibleConfigForm(show, rule?.deductibles);

    useEffect(() => { if (show && !isEdit) fetchKpis(); }, [show, isEdit, fetchKpis]);

    useEffect(() => {
        setForm(rule ? {
            rule_name: rule.rule_name ?? "",
            rule_description: rule.rule_description ?? "",
            kpi_id: rule.kpi_id,
        } : EMPTY_FORM);
        setFormErrors({});
    }, [rule, show]);

    const handleChange = (field: keyof BasicForm) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [field]: field === "kpi_id" ? Number(value) : value }));
        if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleClose = () => {
        setFormErrors({});
        onHide();
    };

    const handleSubmit = async () => {
        const errors: FieldErrors = {};
        const messages: string[] = [];
        if (!form.rule_name.trim()) {
            errors.rule_name = ["Rule name is required."];
            messages.push("Rule name is required.");
        }
        if (!isEdit && !form.kpi_id) {
            errors.kpi_id = ["KPI is required."];
            messages.push("KPI is required.");
        }
        if (messages.length > 0) {
            setFormErrors(errors);
            showValidation(messages);
            return;
        }

        const deductiblePayload = deductibles.buildPayload();
        const base = {
            rule_name: form.rule_name,
            rule_description: form.rule_description || undefined,
            deductibles: deductiblePayload,
        };

        try {
            const response = isEdit
                ? await doUpdate(resolvedNationId, rule.rule_group_id, base)
                : await doCreate(resolvedNationId, { ...base, kpi_id: form.kpi_id });

            if (response.isSuccess()) {
                const data = response.getData();
                showSuccess(data?.message ?? "");
                showSaveWarnings(data);
                if (!isEdit) {
                    setForm(EMPTY_FORM);
                    deductibles.reset();
                }
                handleClose();
                await onSaved?.();
            } else {
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const deductibleMessages = deductibles.applyServerErrors(fieldErrors, deductiblePayload);
                const otherMessages = Object.entries(fieldErrors)
                    .filter(([key]) => !key.startsWith("deductibles"))
                    .flatMap(([, list]) => list ?? [])
                    .filter(Boolean) as string[];
                const list = [...otherMessages, ...deductibleMessages];
                showValidation(list.length ? list : [error?.detail || `Failed to ${isEdit ? "update" : "create"} allocation rule.`]);
                showError(error);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered size="lg" data-testid={testId}>
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Modal.Title className="fw-semibold mb-0">
                        <FiBarChart2 className="me-2" aria-hidden />{isEdit ? "Edit Allocation Rule" : "Create Allocation Rule"}
                    </Modal.Title>
                    {rule && <StatusPill active={rule.is_active} />}
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                <Form>
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Basic Info</h6>
                            <Form.Group className="mb-3" controlId={`${prefix}rule_name`}>
                                <Form.Label>
                                    Rule Name <span className="text-danger">*</span>
                                    <InfoTooltip text="A descriptive name for this allocation rule." />
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter rule name"
                                    value={form.rule_name}
                                    onChange={handleChange("rule_name")}
                                    isInvalid={!!formErrors.rule_name}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.rule_name?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0" controlId={`${prefix}rule_description`}>
                                <Form.Label>Description<InfoTooltip text="Optional notes about this rule." /></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Enter description (optional)"
                                    value={form.rule_description}
                                    onChange={handleChange("rule_description")}
                                    isInvalid={!!formErrors.rule_description}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.rule_description?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">
                                KPI {!isEdit && <span className="text-danger">*</span>}
                                <InfoTooltip text={isEdit ? "The KPI cannot be changed after the rule is created." : "The KPI (income field) this rule applies to."} />
                            </h6>
                            <Form.Group className="mb-0" controlId={`${prefix}kpi_id`}>
                                <Form.Label visuallyHidden>KPI</Form.Label>
                                {isEdit ? (
                                    <Form.Control type="text" value={rule.kpi_name_snapshot ?? "—"} readOnly disabled className="bg-white" />
                                ) : kpisLoading ? (
                                    <div className="d-flex align-items-center"><Spinner animation="border" size="sm" className="me-2" /><span>Loading KPIs…</span></div>
                                ) : kpisError ? (
                                    <div className="text-danger small">Failed to load KPIs.</div>
                                ) : (
                                    <Form.Select
                                        value={form.kpi_id || ""}
                                        onChange={handleChange("kpi_id")}
                                        isInvalid={!!formErrors.kpi_id}
                                    >
                                        <option value="">Select KPI</option>
                                        {kpis.map((k) => (
                                            <option key={k.kpi_id} value={k.kpi_id}>{k.kpi_name}</option>
                                        ))}
                                    </Form.Select>
                                )}
                                <Form.Control.Feedback type="invalid">{formErrors.kpi_id?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    <DeductibleConfigEditor {...deductibles.editorProps} />

                    <div className="d-flex justify-content-end mt-2">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={handleSubmit}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy ? (
                                <><Spinner animation="border" size="sm" className="me-2" />{isEdit ? "Updating…" : "Creating…"}</>
                            ) : isEdit ? (
                                "Update Rule"
                            ) : (
                                <><FiPlus className="me-2" />Create Rule</>
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AllocationRuleFormModal;
